import { useCallback, useEffect, useState } from 'react';
import _get from 'lodash/get';
import _toNumber from 'lodash/toNumber';
import { Task } from 'graphql-common';
import {
  GetUrlParams,
  ORDERING_DIRECTION_DESC,
  PER_PAGE_KEY_DEFAULT,
} from '@lib/enums/urls';
import { indexedDB } from 'utils/indexedDBUtils';
import { ListQueryHookProps } from 'utils/fetch/types';
import { CacheNames } from 'constants/enums';
import { captureException } from 'utils/captureException';
import { SelectOption } from '@lib/components/Select/types';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import { logger } from '@lib/services/logger';

export const fetchDataFromDB = async (
  filters: ListQueryHookProps['filterParams'] = {},
  paginationParams: unknown = {},
) => {
  let filteredData: Task[] = [];
  let paginatedData: Task[] = [];
  try {
    const page = Math.max(
      1,
      _toNumber(_get(paginationParams, GetUrlParams.Page)) || 1,
    );
    const perPage = Math.max(
      1,
      _toNumber(_get(paginationParams, GetUrlParams.PerPage)) ||
        _toNumber(PER_PAGE_KEY_DEFAULT),
    );
    const orderingField = _get(paginationParams, GetUrlParams.OrderingField);
    const orderingDirection = _get(
      paginationParams,
      GetUrlParams.OrderingDirection,
    );
    let db = indexedDB[CacheNames.tasks];

    // Dexie orderBy only works with indexed fields. For non-indexed fields,
    // we fall back to in-memory sorting after fetching the data.
    // Note: 'deadlineAt' is intentionally excluded because Dexie's orderBy
    // silently excludes records where the indexed field is null, which would
    // hide On-Demand tasks (which have deadlineAt: null).
    const indexedFields = ['id', 'createdAt'];
    const canUseIndexSort =
      orderingField &&
      orderingField !== 'completedAt' &&
      indexedFields.includes(orderingField);

    if (canUseIndexSort) {
      db = db.orderBy(orderingField);
      if (orderingDirection === ORDERING_DIRECTION_DESC) {
        db = db.reverse();
      }
    }

    // Apply filters using Dexie
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        db = db.filter((task: Task) => {
          const parsedFilterValue = _get(filters, key) as SelectOption[];

          if (!parsedFilterValue?.length) return true;

          if (key === GetUrlParams.Priority) {
            return parsedFilterValue.some((fv) => fv.value === task[key]);
          }

          if (key === GetUrlParams.SiteCode) {
            return parsedFilterValue.some((fv) => {
              return fv.value === _get(task, ['site', 'code']);
            });
          }

          if (key === GetUrlParams.SiteAreaCode) {
            return parsedFilterValue.some((fv) => {
              return fv.value === _get(task, ['siteArea', 'code']);
            });
          }

          if (key === GetUrlParams.AssetCode) {
            return parsedFilterValue.some((fv) => {
              return fv.value === _get(task, ['asset', 'code']);
            });
          }

          if (
            [
              TasksFilterFieldNames.Site,
              TasksFilterFieldNames.SiteArea,
              TasksFilterFieldNames.Asset,
              TasksFilterFieldNames.FormCategory,
            ].includes(key as TasksFilterFieldNames)
          ) {
            return parsedFilterValue.some((fv) => {
              return fv.value === _get(task, [key, 'id']);
            });
          }

          return true;
        });
      }

      // Only apply the "exclude tasks with assets" filter when siteArea is actively selected
      // (not when it's just a null/empty placeholder in the filter object)
      if (
        key === TasksFilterFieldNames.SiteArea &&
        (filters[key] as SelectOption[])?.length > 0
      ) {
        const assetFilterValue = _get(filters, TasksFilterFieldNames.Asset);
        if (
          !assetFilterValue ||
          (assetFilterValue as SelectOption[])?.length === 0
        ) {
          db = db.filter((task: Task) => !task.asset);
        }
      }

      // Note: Period filter is intentionally not applied for offline/IndexedDB queries.
      // The Downloads tab shows all cached tasks regardless of deadline, and On-Demand
      // tasks have deadlineAt: null which would cause issues with date filtering.
    });

    const offsetValue = Math.max(0, (page - 1) * perPage);

    filteredData = await db.toArray();

    // Apply in-memory sorting for non-indexed fields (e.g. 'name').
    if (orderingField && !canUseIndexSort && orderingField !== 'completedAt') {
      filteredData.sort((a, b) => {
        const valA = String(_get(a, orderingField, '') ?? '').toLowerCase();
        const valB = String(_get(b, orderingField, '') ?? '').toLowerCase();
        const cmp = valA.localeCompare(valB);
        return orderingDirection === ORDERING_DIRECTION_DESC ? -cmp : cmp;
      });
    }

    paginatedData = filteredData.slice(offsetValue, offsetValue + perPage);

    logger.debug('Tasks: IndexedDB query result', {
      filtersApplied:
        Object.keys(filters)
          .filter((k) => filters[k])
          .join(', ') || '(none)',
      totalFiltered: filteredData.length,
      paginatedCount: paginatedData.length,
      page,
      perPage,
      orderingField,
      orderingDirection,
    });
  } catch (e) {
    logger.error('Tasks: IndexedDB query error', {
      error: e instanceof Error ? e.message : String(e),
    });
    captureException(e);
  }
  return {
    filteredData,
    paginatedData,
    limit: paginatedData.length,
    total: filteredData.length,
  };
};

const useTasksIndexedDB = (options: ListQueryHookProps) => {
  const { filterParams, paginationParams } = options;
  const [data, setData] = useState<Task[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [limitValue, setLimitValue] = useState(0);
  const [loading, setLoading] = useState(true);

  // Serialize params to detect actual value changes (not reference changes).
  // filterParams/paginationParams are new object refs on every render,
  // so using them directly in useCallback deps would cause infinite re-renders.
  const serializedFilterParams = JSON.stringify(filterParams);
  const serializedPaginationParams = JSON.stringify(paginationParams);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { paginatedData, total, limit } = await fetchDataFromDB(
        filterParams,
        paginationParams,
      );
      setTotalCount(total);
      setLimitValue(limit);
      setData(paginatedData);
    } catch (error) {
      captureException(error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedFilterParams, serializedPaginationParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, totalCount, loading, limitValue, refetch: fetchData };
};

export default useTasksIndexedDB;
